// =============================================================================
// iTravel 2.0 — i18n Configuration
// =============================================================================
// Sets up i18next with:
//   - English (en) and Polish (pl) translations
//   - Language detection from localStorage, navigator, or auth store
//   - Fallback to English
//   - Interpolation compatible with React (escaping disabled)
//   - Lazy-loaded translation resources (bundled inline for now)
//
// Usage:
//   Import this file once in main.tsx (side-effect import).
//   Then use `useTranslation()` hook from 'react-i18next' in components.
// =============================================================================

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import pl from './locales/pl.json';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SUPPORTED_LANGUAGES = ['en', 'pl'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  pl: 'Polski',
};

const STORAGE_KEY = 'itravel-language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// ---------------------------------------------------------------------------
// Language detection helpers
// ---------------------------------------------------------------------------

/**
 * Reads the persisted language preference from localStorage.
 */
function getStoredLanguage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // localStorage not available (SSR, privacy mode, etc.)
  }
  return null;
}

/**
 * Reads the user's preferred language from the Zustand auth store (if hydrated).
 * We do a direct read from localStorage to avoid importing the store module
 * (which may have side-effects or circular deps at init time).
 */
function getAuthStoreLanguage(): SupportedLanguage | null {
  try {
    const raw = localStorage.getItem('itravel-auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lang =
      parsed?.state?.settings?.preferredLanguage ?? parsed?.state?.user?.preferredLanguage;
    if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      return lang as SupportedLanguage;
    }
  } catch {
    // Malformed or missing — ignore
  }
  return null;
}

/**
 * Detects the best language from browser navigator.
 */
function getBrowserLanguage(): SupportedLanguage | null {
  if (typeof navigator === 'undefined') return null;

  const browserLangs = navigator.languages ?? [navigator.language];
  for (const lang of browserLangs) {
    // Match exact (e.g., 'pl') or prefix (e.g., 'pl-PL' → 'pl')
    const code = lang.split('-')[0]?.toLowerCase();
    if (code && SUPPORTED_LANGUAGES.includes(code as SupportedLanguage)) {
      return code as SupportedLanguage;
    }
  }
  return null;
}

/**
 * Determines the initial language using the following priority:
 * 1. Explicit user choice persisted in localStorage
 * 2. User's preferred language from auth store
 * 3. Browser / OS language
 * 4. Default (English)
 */
function detectLanguage(): SupportedLanguage {
  return getStoredLanguage() ?? getAuthStoreLanguage() ?? getBrowserLanguage() ?? DEFAULT_LANGUAGE;
}

// ---------------------------------------------------------------------------
// i18n initialization
// ---------------------------------------------------------------------------

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    pl: { translation: pl },
  },

  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,

  interpolation: {
    // React already handles XSS protection
    escapeValue: false,
  },

  // Return keys if translation is missing (useful for development)
  returnNull: false,
  returnEmptyString: false,

  // Enable debug logging only in development
  debug: import.meta.env.DEV && false, // flip to `true` to see missing keys in console

  // React-specific settings
  react: {
    // Suspend while translations load (not needed since we bundle them inline)
    useSuspense: false,
  },
});

// ---------------------------------------------------------------------------
// Persist language changes to localStorage
// ---------------------------------------------------------------------------

i18n.on('languageChanged', (lng: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Ignore storage errors
  }

  // Update the HTML lang attribute for accessibility & SEO
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
});

// Set the initial HTML lang attribute
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language;
}

// ---------------------------------------------------------------------------
// Helper: change language programmatically
// ---------------------------------------------------------------------------

export function changeLanguage(lang: SupportedLanguage): Promise<void> {
  return i18n.changeLanguage(lang).then(() => undefined);
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default i18n;
